import {
  CustomReportCategory,
  CustomReportCategoryType
} from '../../../../../../redux/context/customReports/typesCategory'
import { createBuilderNode, generateRandomId } from './utils'
import { NodesById, VisitorContext, State } from './types'

/**
 * Muuntaa CustomReportCategory-taulukon normalisoiduksi tietorakenteeksi
 *
 * @param categories CustomReportCategory-taulukko
 * @param rootType Juurisolmun tyyppi (oletuksena 'root')
 * @returns Objekti, joka sisältää normalisoidun tietorakenteen ja juurisolmun ID:n
 */
export function populateTreeFromData(
  categories: CustomReportCategory[],
  rootType: CustomReportCategoryType = 'root'
): { nodesById: NodesById; rootId: string } {
  const nodesById: NodesById = {}

  // Luo juurisolmu
  const rootId = generateRandomId()
  const rootNode = createBuilderNode(rootId, rootType, { state: State.Root })

  // Muunna juurisolmu normalisoiduksi solmuksi
  nodesById[rootId] = {
    ...rootNode,
    parentId: null,
    childIds: []
  }

  // Luo lapsille ID:t ja lisää ne juurisolmun childIds-taulukkoon
  const rootChildIds = categories.map(category => {
    const id = category.id || generateRandomId()
    return id
  })

  nodesById[rootId].childIds = rootChildIds

  // Rekursiivinen funktio, joka käsittelee solmut ja niiden lapset
  function processNode(
    category: CustomReportCategory,
    parentId: string,
    nodePath: string[] = [],
    context: VisitorContext = { state: State.Root }
  ): void {
    const id = category.id || generateRandomId()

    // Määritä konteksti solmutyypin mukaan
    let nodeContext: VisitorContext
    switch (category.type) {
      case 'company':
        nodeContext = { ...context, company: category.value as string, state: State.Company }
        break
      case 'dimension':
        nodeContext = { ...context, dimension: category.value as string, state: State.Dimension }
        break
      case 'periodGroup':
        nodeContext = { ...context, state: State.PeriodGroup }
        break
      default:
        nodeContext = context
    }

    // Valmistele solmun tiedot normalisoitua rakennetta varten
    const baseNode = createBuilderNode(id, category.type, nodeContext)
    const childIds = (category.children || []).map(child => child.id || generateRandomId())

    // Lisää solmu normalisoituun tietorakenteeseen
    nodesById[id] = {
      ...baseNode,
      ...category,
      id,
      parentId,
      childIds,
      expanded: true
    }

    // Käsittele lapset rekursiivisesti
    ;(category.children || []).forEach(child => {
      // Jos tämä solmu on funktio, merkitään sen lapset funktion jälkeläisiksi
      const childContext = category.type === 'function' ? { ...nodeContext, isFunctionDecendant: true } : nodeContext

      processNode(child, id, [...nodePath, id], childContext)
    })
  }

  // Käsittele jokainen juuritason kategoria
  categories.forEach(category => {
    processNode(category, rootId, [rootId])
  })

  return { nodesById, rootId }
}

/**
 * Esimerkki, kuinka käyttää populateTreeFromData-funktiota CategoryBuilder-komponentissa
 *
 * @param data CustomReportCategory-taulukko
 * @param setNodesById setState-funktio nodesById-tilalle
 * @param setRootIds setState-funktio rootIds-tilalle
 */
export function importDataToTree(
  data: CustomReportCategory[],
  setNodesById: React.Dispatch<React.SetStateAction<NodesById>>,
  setRootIds: React.Dispatch<React.SetStateAction<string[]>>
): void {
  const { nodesById, rootId } = populateTreeFromData(data)

  // Aseta normalisoitu tietorakenne komponenttiin
  setNodesById(nodesById)
  setRootIds([rootId])
}
